import areIdentifiersEqual from './areIdentifiersEqual';

const applyRounding = (value, method) => {
  if (!value && value !== 0) return 0;
  switch (method) {
    case 'CEIL':
      return Math.ceil(value);
    case 'ROUND':
      return Math.round(value);
    case 'EXACT':
    default:
      return value;
  }
};

const calculateAndEnrichItems = (items, source, billingDate) => {
  const billingDateObj = billingDate ? new Date(billingDate) : new Date();
  if (!items || items.length === 0 || !source) {
    return {
      rows: [],
      overflowSources: [],
      coverageStatus: { status: 'ok', messages: [] },
    };
  }

  const sourceMap = new Map(source.map((s) => [s.articleNumber, s]));
  const splitRemainingQuantities = {};
  let potentialOverflows = [];

  const sequentialConsumedMap = new Map();
  source.forEach((s) => sequentialConsumedMap.set(s.articleNumber, 0));

  const getIdentifierGroups = (itemQuantity) => {
    const allIds = itemQuantity?.identifiers || [];
    const reducerIds = itemQuantity?.reducerIdentifiers || [];
    const restIds = itemQuantity?.restIdentifiers || [];
    const standardIds = allIds.filter(
      (id) => !reducerIds.includes(id) && !restIds.includes(id),
    );
    return { allIds, reducerIds, restIds, standardIds };
  };

  // =======================================================================================
  // ====== PASS 1: DISCOVERY & CALCULATION ==============================================
  // =======================================================================================
  let enrichedItemsPass1 = items.map((item, index) => {
    let calculatedQuantity = 0;
    let displayQuantity = 0;

    const getOriginalSourceQuantity = (identifiers) => {
      const allSourcesMap = new Map([
        ...sourceMap.entries(),
        ...potentialOverflows.map((o) => [o.articleNumber, o]),
      ]);
      return (identifiers || []).reduce((sum, id) => {
        return sum + Number(allSourcesMap.get(id)?.quantity || 0);
      }, 0);
    };

    if (item.type === 'STATIC') {
      calculatedQuantity = Number(item.quantity) || 0;
    } else if (item.type === 'DYNAMIC') {
      const { standardIds, restIds } = getIdentifierGroups(item.quantity);

      const getRestQuantity = (id) => {
        const originalQuantity = sourceMap.get(id)?.quantity || 0;
        const consumed = sequentialConsumedMap.get(id) || 0;
        return Math.max(0, originalQuantity - consumed);
      };

      let standardQuantity = 0;
      let restQuantity = 0;

      if (standardIds.length > 0) {
        standardQuantity = getOriginalSourceQuantity(standardIds);
      }

      if (restIds.length > 0) {
        restQuantity = restIds.reduce((sum, id) => {
          if (id.startsWith('overflow_')) {
            return (
              sum +
              Number(
                potentialOverflows.find((o) => o.articleNumber === id)
                  ?.quantity || 0,
              )
            );
          }
          return sum + getRestQuantity(id);
        }, 0);
      }

      let totalCalculatedQuantity = standardQuantity + restQuantity;

      if (!item.quantity?.split) {
        calculatedQuantity = totalCalculatedQuantity;
      } else {
        const uniqueIdentifiersKey =
          standardIds.length > 0 ? [...standardIds].sort().join(',') : null;

        const previousItemInChain = items
          .slice(0, index)
          .reverse()
          .find((prev) => {
            if (prev.type !== 'DYNAMIC') return false;
            const prevStandardIds = getIdentifierGroups(
              prev.quantity,
            ).standardIds;
            return areIdentifiersEqual(prevStandardIds, standardIds);
          });
        const isStartOfNewGroup =
          !previousItemInChain ||
          !previousItemInChain.quantity?.split ||
          previousItemInChain.quantity?.max == null;
        if (isStartOfNewGroup && uniqueIdentifiersKey) {
          splitRemainingQuantities[uniqueIdentifiersKey] = standardQuantity;
        }
        const availableSplitQuantity = uniqueIdentifiersKey
          ? splitRemainingQuantities[uniqueIdentifiersKey] || 0
          : 0;
        const nextItemInChain = items.slice(index + 1).find((future) => {
          if (future.type !== 'DYNAMIC') return false;
          const futureStandardIds = getIdentifierGroups(
            future.quantity,
          ).standardIds;
          return areIdentifiersEqual(futureStandardIds, standardIds);
        });
        const isEndOfSplitGroup =
          item.quantity?.max == null ||
          !nextItemInChain ||
          !nextItemInChain.quantity?.split;

        let allocatedQuantity = isEndOfSplitGroup
          ? availableSplitQuantity
          : Math.min(availableSplitQuantity, item.quantity.max || 0);
        if (!isEndOfSplitGroup && item.quantity?.max == null)
          allocatedQuantity = 0;

        calculatedQuantity = allocatedQuantity + restQuantity;

        if (uniqueIdentifiersKey) {
          splitRemainingQuantities[uniqueIdentifiersKey] -= allocatedQuantity;
        }
      }
    }

    const userFactor = item.factor !== undefined ? Number(item.factor) : 1;
    let rawDisplayQuantity = calculatedQuantity * userFactor;

    displayQuantity = applyRounding(
      rawDisplayQuantity,
      item.rounding || 'EXACT',
    );

    const calculatedQuantityBeforeClamping = displayQuantity;

    const isEndOfSplitGroupCheck =
      !item.quantity?.split ||
      item.quantity?.max == null ||
      !items.slice(index + 1).some((future) => {
        if (future.type !== 'DYNAMIC') return false;
        const { standardIds: currentStandardIds } = getIdentifierGroups(
          item.quantity,
        );
        const { standardIds: futureStandardIds } = getIdentifierGroups(
          future.quantity,
        );
        return areIdentifiersEqual(futureStandardIds, currentStandardIds);
      });

    const shouldApplyClamping = !item.quantity?.split || isEndOfSplitGroupCheck;
    if (shouldApplyClamping) {
      if (item.quantity?.min != null && displayQuantity < item.quantity.min)
        displayQuantity = item.quantity.min;
      if (item.quantity?.max != null && displayQuantity > item.quantity.max)
        displayQuantity = item.quantity.max;
    }

    if (item.type === 'DYNAMIC') {
      const { reducerIds } = getIdentifierGroups(item.quantity);
      const consumedQuantity = displayQuantity / userFactor;

      reducerIds.forEach((id) => {
        if (sequentialConsumedMap.has(id)) {
          sequentialConsumedMap.set(
            id,
            sequentialConsumedMap.get(id) + consumedQuantity,
          );
        }
        if (id.startsWith('overflow_')) {
          const overflow = potentialOverflows.find(
            (o) => o.articleNumber === id,
          );
          if (overflow?.originSourceIdentifiers) {
            const totalOriginalQuantity =
              overflow.originSourceIdentifiers.reduce(
                (sum, originId) =>
                  sum + (sourceMap.get(originId)?.quantity || 0),
                0,
              );

            overflow.originSourceIdentifiers.forEach((originId) => {
              if (
                sequentialConsumedMap.has(originId) &&
                totalOriginalQuantity > 0
              ) {
                const proportion =
                  (sourceMap.get(originId)?.quantity || 0) /
                  totalOriginalQuantity;
                sequentialConsumedMap.set(
                  originId,
                  sequentialConsumedMap.get(originId) +
                    consumedQuantity * proportion,
                );
              }
            });
          }
        }
      });
    }

    const enrichedItem = {
      ...item,
      id: item.uuid,
      displayQuantity,
      effectiveQuantity: displayQuantity,
      isEndOfSplitGroup: isEndOfSplitGroupCheck,
      calculatedQuantityBeforeClamping,
      rounding: item.rounding || 'EXACT',
    };

    const getRootIdentifiers = (ids) => {
      let roots = [];
      ids.forEach((id) => {
        if (id.startsWith('overflow_')) {
          const prevOverflow = potentialOverflows.find(
            (o) => o.articleNumber === id,
          );
          if (prevOverflow && prevOverflow.originSourceIdentifiers) {
            roots.push(
              ...getRootIdentifiers(prevOverflow.originSourceIdentifiers),
            );
          }
        } else {
          roots.push(id);
        }
      });
      return [...new Set(roots)];
    };

    if (
      enrichedItem.articleType === 'SALES_BILL_OF_MATERIAL' &&
      enrichedItem.salesBillOfMaterialItems
    ) {
      enrichedItem.salesBillOfMaterialItems =
        enrichedItem.salesBillOfMaterialItems.map((subItem, subIndex) => {
          let subCalculatedQuantity = 0;
          if (subItem.type === 'DYNAMIC') {
            const { standardIds: subStandardIds, restIds: subRestIds } =
              getIdentifierGroups(subItem.quantity);

            const standardQuantity = getOriginalSourceQuantity(subStandardIds);

            const restQuantity = subRestIds.reduce((sum, id) => {
              if (id.startsWith('overflow_')) {
                return (
                  sum +
                  Number(
                    potentialOverflows.find((o) => o.articleNumber === id)
                      ?.quantity || 0,
                  )
                );
              }
              const originalQuantity = sourceMap.get(id)?.quantity || 0;
              const consumed = sequentialConsumedMap.get(id) || 0;
              return sum + Math.max(0, originalQuantity - consumed);
            }, 0);

            subCalculatedQuantity = standardQuantity + restQuantity;
          } else {
            subCalculatedQuantity = Number(subItem.quantity) || 0;
          }

          const subArticleFactor =
            subItem.articleFactor !== undefined
              ? Number(subItem.articleFactor)
              : 1;

          let targetQuantity = enrichedItem.displayQuantity * subArticleFactor;
          targetQuantity = applyRounding(
            targetQuantity,
            subItem.rounding || 'EXACT',
          );

          if (
            subItem.type === 'DYNAMIC' &&
            subCalculatedQuantity > targetQuantity
          ) {
            const overflow = subCalculatedQuantity - targetQuantity;
            const overflowId = `overflow_${item.uuid}_${subItem.uuid}`;
            potentialOverflows.push({
              articleNumber: overflowId,
              name: `Überlauf von "${subItem.title}" aus "${item.title}"`,
              quantity: overflow,
              isOverflow: true,
              originSourceIdentifiers: getRootIdentifiers(
                subItem.quantity.identifiers,
              ),
            });
          }

          if (subItem.type === 'DYNAMIC') {
            const { reducerIds: subReducerIds } = getIdentifierGroups(
              subItem.quantity,
            );

            subReducerIds.forEach((id) => {
              if (sequentialConsumedMap.has(id)) {
                sequentialConsumedMap.set(
                  id,
                  sequentialConsumedMap.get(id) + targetQuantity,
                );
              }
              if (id.startsWith('overflow_')) {
                const overflow = potentialOverflows.find(
                  (o) => o.articleNumber === id,
                );
                if (overflow?.originSourceIdentifiers) {
                  const totalOriginalQuantity =
                    overflow.originSourceIdentifiers.reduce(
                      (sum, originId) =>
                        sum + (sourceMap.get(originId)?.quantity || 0),
                      0,
                    );

                  overflow.originSourceIdentifiers.forEach((originId) => {
                    if (
                      sequentialConsumedMap.has(originId) &&
                      totalOriginalQuantity > 0
                    ) {
                      const proportion =
                        (sourceMap.get(originId)?.quantity || 0) /
                        totalOriginalQuantity;
                      sequentialConsumedMap.set(
                        originId,
                        sequentialConsumedMap.get(originId) +
                          targetQuantity * proportion,
                      );
                    }
                  });
                }
              }
            });
          }

          return {
            ...subItem,
            id: `${index}-${subIndex}`,
            calculatedQuantity: subCalculatedQuantity,
            displayQuantity: targetQuantity,
            rounding: subItem.rounding || 'EXACT',
            articleFactor: subArticleFactor,
          };
        });
    }
    return enrichedItem;
  });

  const consumedOverflowIds = new Set();
  const allItemsAndSubItemsForOverflowCheck = enrichedItemsPass1.flatMap(
    (item) => [item, ...(item.salesBillOfMaterialItems || [])],
  );
  allItemsAndSubItemsForOverflowCheck.forEach((item) => {
    const { allIds } = getIdentifierGroups(item.quantity || {});
    allIds.forEach((id) => {
      if (id.startsWith('overflow_')) consumedOverflowIds.add(id);
    });
  });

  // =======================================================================================
  // ====== PASS 2: FINAL EVALUATION & STATUS CALCULATION ================================
  // =======================================================================================
  const enrichedItems = enrichedItemsPass1.map((item) => {
    const status = {
      code: item.type === 'DYNAMIC' ? 'ok' : 'static',
      messages: [],
    };
    if (billingDateObj && item.endDate) {
      const itemEnd = new Date(item.endDate);
      if (!isNaN(itemEnd) && itemEnd < billingDateObj) {
        status.messages.push(
          `Enddatum der Position (${itemEnd.toLocaleDateString('de-DE')}) liegt vor dem Abrechnungsdatum (${billingDateObj.toLocaleDateString('de-DE')}).`,
        );
        if (status.code === 'ok') {
          // If an extension is configured, downgrade to warning instead of error
          if (item.extensionQuantity != null && item.extensionUnit) {
            status.code = 'warning';
          } else {
            status.code = 'error';
          }
        }
      }
    }
    if (item.type === 'DYNAMIC') {
      const { allIds } = getIdentifierGroups(item.quantity);
      if (allIds.length === 0)
        status.messages.push('Keine Quell-Artikel (Identifier) definiert.');
      const allAvailableSourcesMap = new Map([
        ...sourceMap.entries(),
        ...potentialOverflows.map((o) => [o.articleNumber, o]),
      ]);
      const missing = allIds.filter((id) => !allAvailableSourcesMap.has(id));
      if (missing.length > 0) {
        status.messages.push(
          `Ein oder mehrere Quell-Artikel konnten nicht gefunden werden: ${missing.join(', ')}.`,
        );
      }

      const shouldApplyMinMaxWarnings =
        !item.quantity?.split || item.isEndOfSplitGroup;
      if (shouldApplyMinMaxWarnings) {
        if (
          item.quantity?.max != null &&
          item.calculatedQuantityBeforeClamping > item.quantity.max
        )
          status.messages.push(
            `Maximum überschritten: Max: ${item.quantity.max} || Ist: ${item.calculatedQuantityBeforeClamping.toFixed(2)} || => auf Max gekürzt.`,
          );
        if (
          item.quantity?.min != null &&
          item.calculatedQuantityBeforeClamping < item.quantity.min
        ) {
          status.messages.push(
            `Minimum unterschritten: Min: ${item.quantity.min} || Ist: ${item.calculatedQuantityBeforeClamping.toFixed(2)} || => auf Min angehoben.`,
          );
          status.code = 'underflow';
        }
      }
    }
    if (
      item.articleType === 'SALES_BILL_OF_MATERIAL' &&
      item.salesBillOfMaterialItems
    ) {
      item.salesBillOfMaterialItems = item.salesBillOfMaterialItems.map(
        (subItem) => {
          const subStatus = { code: 'ok', messages: [] };
          const targetQuantity = subItem.displayQuantity;
          let subAvailableQuantity =
            subItem.type === 'STATIC'
              ? subItem.quantity || 0
              : subItem.calculatedQuantity || 0;

          if (subAvailableQuantity < targetQuantity) {
            subStatus.code =
              subItem.type === 'STATIC' ? 'underflow-static' : 'underflow';
            subStatus.messages.push(
              `Unterdeckung: ${(targetQuantity - subAvailableQuantity).toFixed(2)}`,
            );
          }
          if (billingDateObj && subItem.endDate) {
            const subEnd = new Date(subItem.endDate);
            if (!isNaN(subEnd) && subEnd < billingDateObj) {
              subStatus.messages.push(
                `Enddatum der Unterposition (${subEnd.toLocaleDateString('de-DE')}) liegt vor dem Abrechnungsdatum (${billingDateObj.toLocaleDateString('de-DE')}).`,
              );
              if (subStatus.code === 'ok') {
                if (
                  subItem.extensionQuantity != null &&
                  subItem.extensionUnit
                ) {
                  subStatus.code = 'warning';
                } else {
                  subStatus.code = 'error';
                }
              }
            }
          }

          return {
            ...subItem,
            status: subStatus,
            totalPrice:
              (subItem.unitPrice || 0) *
              targetQuantity *
              (subItem.discountPercentage
                ? 1 + subItem.discountPercentage / 100
                : 1),
          };
        },
      );
      const subMessages = item.salesBillOfMaterialItems.flatMap((s) =>
        s.status && s.status.code !== 'ok' && s.status.code !== 'underflow'
          ? s.status.messages.map((m) => `${s.title}: ${m}`)
          : [],
      );
      if (subMessages.length > 0) {
        status.messages.push(...subMessages);
        if (status.code === 'ok') status.code = 'error';
      }
    }

    const discountValue = item.discountPercentage || 0;

    if (status.messages.length > 0 && status.code === 'ok')
      status.code = 'warning';

    // 1. calculate total price of the contractItem itself (parent)
    // 2. add total prices of all sub-items IF useSalesBillOfMaterialItemPrices at parent is true
    return {
      ...item,
      totalPrice:
        (item.unchangedUnitPrice || 0) *
          item.displayQuantity *
          (discountValue ? 1 + discountValue / 100 : 1) +
        (item.useSalesBillOfMaterialItemPrices
          ? item.salesBillOfMaterialItems?.reduce(
              (acc, sub) => acc + (sub.totalPrice || 0),
              0,
            )
          : 0),
      status,
      discountPercentage: discountValue,
    };
  });

  // =======================================================================================
  // ====== PASS 3: GLOBAL COVERAGE CHECK (REDUX) =========================================
  // =======================================================================================
  const consumedQuantities = new Map();
  sourceMap.forEach((_, key) => consumedQuantities.set(key, 0));

  const getOriginalSourceQuantityBase = (identifiers) =>
    (identifiers || []).reduce(
      (sum, id) => sum + Number(sourceMap.get(id)?.quantity || 0),
      0,
    );

  const allConsumers = enrichedItems.flatMap((item) => [
    { consumer: item, isSubItem: false },
    ...(item.salesBillOfMaterialItems || []).map((sub) => ({
      consumer: sub,
      isSubItem: true,
    })),
  ]);

  allConsumers.forEach(({ consumer, isSubItem }) => {
    if (consumer?.type !== 'DYNAMIC') return;

    const { standardIds, reducerIds, restIds } = getIdentifierGroups(
      consumer.quantity,
    );

    const consumerFactor =
      !isSubItem && consumer.factor !== undefined ? Number(consumer.factor) : 1;
    const consumedQtyRaw = isSubItem
      ? consumer.displayQuantity
      : consumer.effectiveQuantity;
    const consumedQty =
      consumerFactor === 0 ? 0 : consumedQtyRaw / consumerFactor;

    const baseStandardIds = standardIds.filter(
      (id) => !id.startsWith('overflow_'),
    );
    const totalOriginalStandard =
      getOriginalSourceQuantityBase(baseStandardIds);

    baseStandardIds.forEach((id) => {
      if (consumedQuantities.has(id) && totalOriginalStandard > 0) {
        const proportion =
          (sourceMap.get(id)?.quantity || 0) / totalOriginalStandard;
        consumedQuantities.set(
          id,
          consumedQuantities.get(id) + consumedQty * proportion,
        );
      }
    });

    const baseReducerIds = reducerIds.filter(
      (id) => !id.startsWith('overflow_'),
    );
    const totalOriginalReducers = getOriginalSourceQuantityBase(baseReducerIds);

    baseReducerIds.forEach((id) => {
      if (consumedQuantities.has(id) && totalOriginalReducers > 0) {
        const proportion =
          (sourceMap.get(id)?.quantity || 0) / totalOriginalReducers;
        consumedQuantities.set(
          id,
          consumedQuantities.get(id) + consumedQty * proportion,
        );
      }
    });

    const baseRestIds = restIds.filter((id) => !id.startsWith('overflow_'));
    const totalOriginalRest = getOriginalSourceQuantityBase(baseRestIds);

    baseRestIds.forEach((id) => {
      if (consumedQuantities.has(id) && totalOriginalRest > 0) {
        const proportion =
          (sourceMap.get(id)?.quantity || 0) / totalOriginalRest;
        consumedQuantities.set(
          id,
          consumedQuantities.get(id) + consumedQty * proportion,
        );
      }
    });

    [...standardIds, ...reducerIds, ...restIds]
      .filter((id) => id.startsWith('overflow_'))
      .forEach((id) => {
        const overflow = potentialOverflows.find((o) => o.articleNumber === id);
        if (overflow?.originSourceIdentifiers) {
          const reusedAmount = Math.min(consumedQty, overflow.quantity);
          const totalRoots = getOriginalSourceQuantityBase(
            overflow.originSourceIdentifiers,
          );

          overflow.originSourceIdentifiers.forEach((originId) => {
            if (consumedQuantities.has(originId) && totalRoots > 0) {
              const proportion =
                (sourceMap.get(originId)?.quantity || 0) / totalRoots;
              consumedQuantities.set(
                originId,
                consumedQuantities.get(originId) + reusedAmount * proportion,
              );
            }
          });
        }
      });
  });

  const internalBillingMessages = enrichedItems.flatMap((item) =>
    item.status?.code !== 'ok' && item.status?.code !== 'underflow'
      ? item.status.messages.map((msg) => `[${item.title}]: ${msg}`)
      : [],
  );
  const coverageMessages = [];
  sourceMap.forEach((sourceArticle, articleNumber) => {
    const totalConsumed = consumedQuantities.get(articleNumber) || 0;
    const deficit = sourceArticle.quantity - totalConsumed;
    if (deficit > 0.01) {
      coverageMessages.push(
        `Nicht Abgedeckt: ${deficit.toFixed(2)} von ${sourceArticle.quantity.toFixed(2)} || ${sourceArticle.name} || ${articleNumber}`,
      );
    }
  });
  potentialOverflows.forEach((overflow) => {
    if (!consumedOverflowIds.has(overflow.articleNumber)) {
      coverageMessages.push(
        `Unbehandelter Split-Überlauf: ${overflow.quantity.toFixed(2)} || ${overflow.name}`,
      );
    }
  });

  const allMessages = [...coverageMessages, ...internalBillingMessages];
  let coverageStatus = { status: 'ok', messages: [] };
  if (allMessages.length > 0)
    coverageStatus = { status: 'warning', messages: allMessages };

  return {
    rows: enrichedItems,
    overflowSources: potentialOverflows,
    coverageStatus,
  };
};

export default calculateAndEnrichItems;
